import useJsonLd from './useJsonLd';
import { SITE_ORIGIN, SOCIAL_LINKS, SITE_NAME, CONTACT_EMAIL } from '../../config/site';

/* ==========================================================================
   OmniversailGlobalJsonLd — app-wide authority signals.
   Mounted once in App.js (inside the router) so every route — marketing,
   language, curriculum, comps, account — carries the same Organization +
   WebSite/SearchAction JSON-LD. This is the GEO "who is this?" anchor that
   AI search needs to attribute the brand correctly.

   Reuses the existing useJsonLd hook (client-side <head> injection).
   ========================================================================== */

const ORIGIN = SITE_ORIGIN || 'https://omniversail.com';
const LOGO = `${ORIGIN}/logo.png`;

export default function OmniversailGlobalJsonLd() {
  // ── Organization: the brand authority node ────────────────────────────────
  const organization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Omniversail',
    alternateName: SITE_NAME || 'Omniversail Language Center',
    url: ORIGIN,
    logo: LOGO,
    image: LOGO,
    description:
      'Omniversail is a multilingual learning and exam-prep platform covering 150+ languages and 15 academic subjects — K-12 through graduate school — built on the FSI/DLI method of intensive listening, pattern drills, free production and adaptive spaced review.',
    foundingDate: '2023',
    areaServed: 'Worldwide',
    contactPoint: {
      '@type': 'ContactPoint',
      email: CONTACT_EMAIL || 'contact@omniversail.com',
      contactType: 'customer support',
      availableLanguage: ['en', 'zh'],
    },
    sameAs: Array.from(
      new Set([
        ...(SOCIAL_LINKS || []).map((s) => s.href).filter(Boolean),
        'https://www.mgv.gallery',
        'https://openbadge.me',
        'https://chefkunming.com',
        'https://visit-kunming.com',
      ])
    ),
  };

  // ── WebSite + SearchAction: makes the brand searchable/attributable ─────────
  const webSite = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Omniversail',
    alternateName: SITE_NAME || 'Omniversail Language Center',
    url: `${ORIGIN}/`,
    inLanguage: ['en', 'zh'],
    publisher: {
      '@type': 'Organization',
      name: 'Omniversail',
      url: ORIGIN,
      logo: LOGO,
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${ORIGIN}/comps?subject={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  useJsonLd('ov-org-jsonld', organization);
  useJsonLd('ov-website-jsonld', webSite);

  return null;
}
